import { useCallback, useEffect } from 'react'
import { useLocale, useTranslations, TranslationValues } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import moment from 'moment'

import 'moment/locale/en-gb'
import 'moment/locale/ru'

import { usePathname, useRouter } from '@/i18n'
import { generatePath, setStorageValue } from '@/utils'
import { LANG_KEY } from '@/constants'
import { Paths, TLang } from '@/types'

import enTranslation from '@/locale/en.json'
import ruTranslation from '@/locale/ru.json'

export const resources = {
  en: { ...enTranslation },
  ru: { ...ruTranslation },
} as const

type rootLevelKeys = (typeof resources)[TLang]
export type DepthKeys = Paths<rootLevelKeys, 4>

const momentLocale = {
  ru: 'ru-RU',
  en: 'en-EN',
}

export const useLanguages = () => {
  const router = useRouter()
  const appLang = useLocale()
  const _t = useTranslations()
  const pathname = usePathname()
  const params = useSearchParams()

  const changeLanguage = useCallback(
    (locale: TLang) => {
      setStorageValue(LANG_KEY, locale)
      router.replace(
        generatePath(
          pathname,
          undefined,
          // @ts-ignore
          params
            .keys()
            // @ts-ignore
            .reduce((prev, key) => [...prev, { [key]: params.get(key) }], []),
        ),
        { locale },
      )
      moment.locale(momentLocale[locale])
    },
    [router, pathname, params],
  )

  const t = useCallback(
    (_key: DepthKeys, values?: TranslationValues & { default?: string }) => {
      if (!_key) return ''
      if (!isNaN(+_key)) return _key
      const text = _t(_key, values)
      return (text === _key && !!values?.default ? values.default : text) ?? ''
    },
    [_t],
  )

  useEffect(() => {
    setStorageValue(LANG_KEY, appLang)
    moment.locale(momentLocale[appLang as TLang])
  }, [appLang, changeLanguage])

  return {
    t,
    rich: _t.rich,
    changeLanguage,
    appLang: appLang as TLang,
    isRussian: appLang === 'ru',
    momentLocale: momentLocale[appLang as TLang],
  }
}
